import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tag } from 'antd';

const LocationResolver = ({ lat, lng, value, field, apiaryId }) => {
    const [resolvedValue, setResolvedValue] = useState(value);
    const [loading, setLoading] = useState(false);

    // Helper to check if a string looks like a coordinate
    const isCoordinate = (str) => {
        if (!str) return true; // Treat empty as needing resolution if lat/lng exist
        // Check if it looks like a float number
        return /^-?\d+(\.\d+)?$/.test(String(str).trim());
    };

    const isLocationValueInvalid = (val, fld) => {
        if (!val) return true;
        const lower = String(val).toLowerCase().trim();
        
        // Check for obvious address/road keywords
        const addressKeywords = [
            'road', 'street', 'lane', 'gali', 'path', 'highway', 'chowk',
            'library', 'masjid', 'temple', 'church', 'school', 'college', 'university',
            'hospital', 'clinic', 'shop', 'store', 'market', 'bazaar', 'building',
            'apartment', 'villa', 'house', 'plot', 'near', 'opposite', 'behind',
            'beside', 'floor', 'block', 'phase', 'sector', 'nagar', 'colony', 'enclave'
        ];
        
        if (addressKeywords.some(keyword => lower.includes(keyword))) {
            return true;
        }
        
        if (fld === 'state') {
            const VALID_INDIAN_STATES = new Set([
                'andhra pradesh', 'arunachal pradesh', 'assam', 'bihar', 'chhattisgarh', 'goa', 'gujarat',
                'haryana', 'himachal pradesh', 'jharkhand', 'karnataka', 'kerala', 'madhya pradesh',
                'maharashtra', 'manipur', 'meghalaya', 'mizoram', 'nagaland', 'odisha', 'punjab',
                'rajasthan', 'sikkim', 'tamil nadu', 'telangana', 'tripura', 'uttar pradesh',
                'uttarakhand', 'west bengal', 'delhi', 'jammu & kashmir', 'jammu and kashmir', 'ladakh',
                'puducherry', 'chandigarh', 'dadra and nagar haveli', 'daman and diu', 'lakshadweep',
                'andaman and nicobar islands'
            ]);
            return !VALID_INDIAN_STATES.has(lower);
        }
        
        return false;
    };

    const getCacheKey = () => `apiary_loc_${apiaryId}_${field}`;

    useEffect(() => {
        const looksLikeInvalid = isLocationValueInvalid(value, field);

        // If we have a valid text value that isn't a coordinate and isn't invalid, use it
        if (value && !isCoordinate(value) && value !== '-' && !looksLikeInvalid) {
            setResolvedValue(value);
            return;
        }

        // If no lat/lng, we can't resolve anything
        if (!lat || !lng) {
            setResolvedValue(value || '-');
            return;
        }

        // Check Cache
        const cached = localStorage.getItem(getCacheKey());
        if (cached && !isLocationValueInvalid(cached, field)) {
            setResolvedValue(cached);
            return;
        }

        // Fetch from API
        const fetchLocation = async () => {
            setLoading(true);
            try {
                // Rate limiting precaution: minimal delay if multiple mount at once could be handled by queue, 
                // but for now relying on browser's request handling and OSM's tolerance.
                const res = await axios.get(
                    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10`
                );

                if (res.data && res.data.address) {
                    let newValue = '';
                    const addr = res.data.address;

                    // Map fields based on OSM response structure
                    if (field === 'district') {
                        newValue = addr.state_district || addr.county || addr.district || '';
                    } else if (field === 'state') {
                        newValue = addr.state || '';
                    } else if (field === 'village') {
                        newValue = addr.village || addr.town || addr.city || '';
                    } else if (field === 'block') {
                        newValue = addr.suburb || addr.neighbourhood || '';
                    } else if (field === 'name') {
                        newValue = res.data.display_name.split(',')[0]; // First part of address
                    }

                    if (newValue) {
                        setResolvedValue(newValue);
                        localStorage.setItem(getCacheKey(), newValue);
                    } else {
                        // Fallback to display name if specific field missing?
                        // For now just keep original if nothing found
                    }
                }
            } catch (error) {
                console.error("Reverse geocoding failed", error);
            } finally {
                setLoading(false);
            }
        };

        // Add a small random delay to prevent hitting rate limits if many components mount at once
        const timer = setTimeout(() => {
            fetchLocation();
        }, Math.random() * 2000);

        return () => clearTimeout(timer);

    }, [lat, lng, value, field, apiaryId]);

    if (loading) return <span style={{ color: '#aaa' }}>Resolving...</span>;

    return <span>{resolvedValue}</span>;
}

export default LocationResolver;
